// "Wall systems by position" — the ONE editor the Room Detail Flyout and the Room Template
// editor both render, so the two cannot drift. A room (or template) may name a different
// wall system for each kind of wall it has:
//   Exterior — the room's own wall, no neighbor on the other side
//   Shared   — one wall two rooms stand on (a shared wall is filled only when both agree)
//   Cavity   — this room's own face of a two-wall cavity
// A blank row INHERITS (the caller says what it resolves to, so nothing is a mystery); the
// older "all walls" system stays the fallback for any position left blank. Pair rules in
// Setup ▸ Construction Standards still win over everything here.
import { WALL_POSITIONS, parseWallSystemsJson, encodeWallSystems, effectiveWallSystems } from "../utils/takeoffGeometry";

export const POSITION_LABELS = {
  exterior: { label: "Exterior walls", hint: "Walls with no neighbor on the other side." },
  shared:   { label: "Shared walls", hint: "A wall two rooms share. Filled only when both rooms name the same system; otherwise it is left for review." },
  cavity:   { label: "Cavity faces", hint: "This room's own face of a wall with a cavity between it and its neighbor." },
};

/** "W-1 — 2" Wall Panel System" for a { kind, itemId, assemblyId } system, from the catalog. */
export function wallSystemLabel(sys, catalog) {
  if (!sys) return null;
  const e = sys.kind === "item"
    ? (catalog?.items || []).find(i => i.ItemID === sys.itemId)
    : (catalog?.assemblies || []).find(a => a.AssemblyID === sys.assemblyId);
  if (!e) return sys.name || sys.templateName || "a catalog row";
  return `${e.Code ? `${e.Code} — ` : ""}${sys.kind === "item" ? e.ItemName : e.Description}${sys.kind === "assembly" ? " · assembly" : ""}`;
}

/**
 * What each position resolves to when THIS owner leaves it blank, as text for the "Inherit" option.
 * `own` = the owner's own JSON (ignored); room: falls to its "all walls" then the template's; template: its own "all walls".
 */
export function inheritedLabels({ room, template, templatesById, catalog }) {
  const out = {};
  if (room) {
    const w = effectiveWallSystems({ ...room, WallSystemsJson: null }, templatesById);
    for (const p of WALL_POSITIONS) out[p] = w.byPosition[p] ? `${wallSystemLabel(w.byPosition[p], catalog)} (${w.byPosition[p].source === "room" ? "this room's all-walls system" : `template ${w.byPosition[p].scope === "position" ? "default for this position" : "all-walls default"}`})` : null;
  } else if (template) {
    const own = template.WallKind && (template.WallKind === "item" ? template.WallItemID : template.WallAssemblyID)
      ? { kind: template.WallKind, itemId: template.WallKind === "item" ? +template.WallItemID : null, assemblyId: template.WallKind === "assembly" ? +template.WallAssemblyID : null } : null;
    for (const p of WALL_POSITIONS) out[p] = own ? `${wallSystemLabel(own, catalog)} (all-walls system)` : null;
  }
  return out;
}

/**
 * value: the WallSystemsJson text (or null). options: [{ key: 'item:7', kind, id, label }].
 * inherited: { exterior, shared, cavity } label text or null. onChange(jsonTextOrNull).
 */
export default function WallSystemsEditor({ value, options = [], inherited = {}, disabled = false, onChange, compact = false }) {
  const cur = parseWallSystemsJson(value);
  const keyOf = (s) => s ? `${s.kind}:${s.kind === "item" ? s.itemId : s.assemblyId}` : "";
  const setPos = (pos, key) => {
    const next = { ...cur };
    if (!key) delete next[pos];
    else { const [kind, id] = key.split(":"); next[pos] = { kind, itemId: kind === "item" ? +id : null, assemblyId: kind === "assembly" ? +id : null }; }
    onChange(encodeWallSystems(next));
  };
  const lbl = { display: "block", fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 };
  const inp = { width: "100%", fontSize: 12, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "4px 6px", background: "var(--surface)", color: "var(--text)", boxSizing: "border-box" };
  return (
    <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "repeat(auto-fit, minmax(220px, 1fr))", gap: "8px 12px" }}>
      {WALL_POSITIONS.map(p => {
        const own = keyOf(cur[p]);
        const stale = own && !options.some(o => o.key === own);   // a stored choice no longer offered (retired / re-layered) stays visible
        return (
          <div key={p}>
            <label style={lbl} title={POSITION_LABELS[p].hint}>{POSITION_LABELS[p].label}</label>
            <select value={own} disabled={disabled} onChange={e => setPos(p, e.target.value)} title={POSITION_LABELS[p].hint}
              style={{ ...inp, color: own ? "var(--text)" : "var(--text3)" }}>
              <option value="">{inherited[p] ? `Inherit — ${inherited[p]}` : "Inherit — none set"}</option>
              {stale && <option value={own}>{own} (not in the wall list)</option>}
              {options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
        );
      })}
    </div>
  );
}
